// PO7-S2 (docs/build-briefs/PO7-S2-invoice-ocr-draft.md). Pure helpers for the
// invoice card's reading status and read-only draft: the plain words for each
// status, which action a status offers, money from cents, confidence as a
// whole percentage, and the duplicate invoice number warning. No DOM, no
// network. Money is only ever displayed here, never typed.

import { formatInvoiceDate } from "./invoiceAttachmentsDisplay.ts";

export type ReadingStatus = "pending" | "drafted" | "failed" | "unavailable";

export function describeReading(status: ReadingStatus): string {
  switch (status) {
    case "pending":
      return "Reading this invoice...";
    case "drafted":
      return "Draft ready";
    case "failed":
      return "Could not read this invoice";
    case "unavailable":
      return "Reading not available";
  }
}

/** What a status offers: a draft can be viewed by anyone who can see the
 * list; a failed or unavailable reading can be retried only by someone who
 * may attach (the server enforces the same). */
export function readingAction(
  status: ReadingStatus,
  options: { canRetry: boolean },
): "view" | "retry" | "none" {
  if (status === "drafted") {
    return "view";
  }
  if ((status === "failed" || status === "unavailable") && options.canRetry) {
    return "retry";
  }
  return "none";
}

const NOT_READ = "Not read";

/** `20800` is `$208.00`; a credit keeps its sign; an unread value says so. */
export function formatDraftMoney(cents: number | null): string {
  if (cents === null) {
    return NOT_READ;
  }
  const negative = cents < 0;
  const absolute = Math.abs(cents);
  const dollars = Math.floor(absolute / 100)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const remainder = String(absolute % 100).padStart(2, "0");
  return `${negative ? "-" : ""}$${dollars}.${remainder}`;
}

/** `0.97` is `97%`; a missing confidence says so. */
export function formatConfidence(confidence: number | null): string {
  return confidence === null ? NOT_READ : `${Math.round(confidence * 100)}%`;
}

export interface DraftDuplicateInfo {
  orderId: string;
  vendorName: string;
  placedAt: string | null;
}

/** The warning for an invoice number already read on another invoice of the
 * same vendor (the review mockup's wording, softened: S2 only flags). */
export function describeDraftDuplicate(
  invoiceNumber: string | null,
  duplicate: DraftDuplicateInfo,
): { title: string; detail: string } {
  const placed =
    duplicate.placedAt === null ? "" : `, placed ${formatInvoiceDate(duplicate.placedAt)}`;
  return {
    title: `Invoice ${invoiceNumber ?? "number"} was already read on another invoice.`,
    detail:
      `The same number is on an invoice for the ${duplicate.vendorName} order${placed}. ` +
      "Check that this is not the same invoice twice.",
  };
}
